import PropTypes from 'prop-types';
import React, {useContext} from 'react';

import LookupField from '../../ui/lookup/LookupField';
import {TextAlignLeftRenderer} from '../SmartTableRenderers';

import ApplicationContext from '../../ApplicationContext';
import {TableColumn} from '../../tableSettings';
import { SQLIndex } from '../../fms.core/constants';
import { LookupPort } from '../../fms.data/lookup';
import { ListResult } from '../../framework.core/data/ListResult';
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';

function OceanUnPortLookupField({
  initialValue = '',
  onSelect = () => {},
  placeholder = undefined,
  onTextChange = () => {},
  disabled = false,
  lookupId = undefined,
  onTabNext = undefined,
  onTabPrevious = undefined,
  searchBy = 'un',
  searchMatchOnTab = false,
  hasLookupValue = false,
  maxLength = undefined,
  onBlur,
  ...other
}: {
  disabled?: boolean,
  initialValue?: string,
  onSelect?: (x:LookupPort) => void,
  placeholder?: string|undefined,
  onTextChange?: (text:string|undefined) => void,
  lookupId?: string|undefined,
  onTabNext?: (() => boolean)|undefined,
  onTabPrevious?: (() => boolean)|undefined,
  searchBy: 'portName' | 'us' | 'un' | 'unWithPortName',
  searchMatchOnTab?: boolean,
  hasLookupValue?: boolean,
  maxLength?: number|undefined,
  onBlur?: () => void,
  [x:string]: any,
}) {
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();

  function getProps() {
    let queryTerm = 'unPortName';
    let labelProp = 'unCode';
  
    switch (searchBy) {
      case 'portName':
        queryTerm = 'portNameLookup';
        labelProp = 'portName';
        break;
      case 'un':
        queryTerm = 'unPortName'; 
        labelProp = 'code';
        break;
      case 'us':
        queryTerm = 'usPortName';
        labelProp = 'usCode';
        break;
      case 'unWithPortName': 
        queryTerm = 'unCodeWithPortName';
        labelProp = 'unCodeWithPortName';
        break;
      default:
        break;
    }

    return { queryTerm, labelProp };
  }

  async function queryPorts(queryString:string|null) {
     const apiResult:ListResult|null = await appContext.api.getLookupItems(SQLIndex.POLCode, queryString ?? '');
     //return apiResult?.data?.entities;
     if(!apiResult || !apiResult.success){
      showGlobalOkDialog(<p>{apiResult?.message || 'Could not get list !!!'}</p>);
      return [];
     }
     return apiResult.entities;

    // const search = {
    //   general1: getProps().queryTerm,
    //   generalText1: queryString,
    //   orderBy: 'portName',
    //   orderByAscending: true,
    //   pageSize: 100,
    // };

    // const apiResult = await appContext.api.getCodesOceanUnPorts(search);

    // return apiResult && apiResult.data ? apiResult.data : [];
  }

  function onCancel() {
  }

  function filter(data:Array<LookupPort>, filterText:string) {
    return data.filter(x =>
      (x.code && x.code.toLowerCase().includes(filterText.toLowerCase())) ||
      (x.portName && x.portName.toLowerCase().includes(filterText.toLowerCase())) ||
      (x.usCode && x.usCode.toLowerCase().includes(filterText.toLowerCase()))
    );
  }

  const columns:Array<TableColumn> = [
    { Header: 'UN Port Name', accessor: 'portName', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 400, Cell: TextAlignLeftRenderer },
    { Header: 'UN Code', accessor: 'code', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 80, Cell: TextAlignLeftRenderer },
    { Header: 'US Code', accessor: 'usCode', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 80, Cell: TextAlignLeftRenderer },
  ];

  return (
    <LookupField
      lookupId={lookupId}
      onTabNext={onTabNext}
      onTabPrevious={onTabPrevious}
      disabled={disabled}
      query={queryPorts}
      width="576px"
      columns={columns} 
      onSelect={onSelect}
      onCancel={onCancel}
      labelProp={getProps().labelProp}
      initialValue={initialValue}
      placeholder={placeholder}
      onTextChange={onTextChange}
      filter={filter}
      searchMatchOnTab={searchMatchOnTab}
      hasLookupValue={hasLookupValue}
      maxLength={maxLength}
      onBlur={onBlur}
      {...other}
    />
  );
}

OceanUnPortLookupField.defaultProps = {
  onSelect: () => {},
  onTextChange: () => {},
  disabled: false,
  searchBy: 'un',
  searchMatchOnTab: false,
};

OceanUnPortLookupField.propTypes = {
  initialValue: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onTextChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  lookupId: PropTypes.string,
  onTabNext: PropTypes.func,
  onTabPrevious: PropTypes.func,
  searchBy: PropTypes.oneOf(['portName', 'us', 'un', 'unWithPortName']).isRequired,
  searchMatchOnTab: PropTypes.bool.isRequired,
  hasLookupValue: PropTypes.bool,
  maxLength: PropTypes.number,
  onBlur: PropTypes.func
};


export default React.memo(OceanUnPortLookupField);
